import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import _ from 'lodash';
import './style.scss';
import CPPTimelineTask from 'components/Patient/CPP/CPPTimeline/CPPTimelineTask';
import CPPTimelineImmunization from 'components/Patient/CPP/CPPTimeline/CPPTimelineImmunization';
import CPPTimelineDocument, {
  EditDocumentModal,
  EditDocumentModalContext,
} from 'components/Patient/CPP/CPPTimeline/CPPTimelineDocument';
import CPPTimelineReferral from 'components/Patient/CPP/CPPTimeline/CPPTimelineReferral';
import CPPTimelinePrescription from 'components/Patient/CPP/CPPTimeline/CPPTimelinePrescription';
import CPPTimelineAppointment from 'components/Patient/CPP/CPPTimeline/CPPTimelineAppointment';
import CPPTimelineAppointmentNote from 'components/Patient/CPP/CPPTimeline/CPPTimelineAppointmentNote';
import CPPTimelineAppointmentNoteDraft from 'components/Patient/CPP/CPPTimeline/CPPTimelineAppointmentNoteDraft';
import Icon from 'components/Utils/NewIcon';
import CalendarUtils from 'services/CalendarUtils';
import AppointmentUtils, { APPOINTMENT_STATUS } from 'services/AppointmentUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import CPPUtils from 'services/CPPUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import CPPTimelinePrescriptionRefill from 'components/Patient/CPP/CPPTimeline/CPPTimelinePrescriptionRefill';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import CPPTimelineFax from 'components/Patient/CPP/CPPTimeline/CPPTimelineFax';
import IconButton from '@material-ui/core/IconButton';
import LazyLoad from 'react-lazyload';
import { useAppSelector } from 'careand-redux/store';
import CPPTimelineAdministeredDrug from 'components/Patient/CPP/CPPTimeline/CPPTimelineAdministeredDrug';
import CPPTimelineCommunicationNote from 'components/Patient/CPP/CPPTimeline/CPPTimelineCommunicationNote';
import TaskEditModal from 'components/Tasks/Utils/TaskEditModal';
import { GetStaff, IsPhysician, IsTypeNP, IsTypePhlebotomist } from 'careand-redux/selectors';
import CPPTimelineMedicalOrder from './CPPTimelineMedicalOrder';
import { PhysicianPhysicianTypeEnum, UserRoleEnum } from 'generated/api';
import MedicalOrderModal from 'components/Modals/MedicalOrder';
import CPPTimelineReferralRequest from 'components/Patient/CPP/CPPTimeline/CPPTimelineReferralRequest';
import CPPTimelineCall from 'components/Patient/CPP/CPPTimeline/CPPTimelineCall';
import TaskUtils from 'services/TaskUtils';
import CPPTimelineHandover from 'components/Patient/CPP/CPPTimeline/CPPTimelineHandover';
import ReactMarkdown from 'react-markdown';
import { PERMISSIONS } from 'services/StaffUtils';
import { Li } from 'components/Chat/ChatWindow/ChatMessage';

const dateFormat = 'MMM Do YYYY';
const DEFAULT_OBJECT = {};
const DEFAULT_ARRAY = [];
const DEFAULT_VISIBLE = 50;

const CPPTimeline = (props) => {
  const [confirmationDialog, setConfirmationDialog] = useState({
    title: undefined,
    message: undefined,
  });

  const [numberVisible, setNumberVisible] = useState(DEFAULT_VISIBLE);
  const [search, setSearch] = useState();
  const [task, setTask] = useState();
  const localCppTop = useRef();
  const cppTop = props.cppTopRef ?? localCppTop;

  const { patient, setActiveCPPTab } = props;

  const patientId = patient?.id;

  const physicians = useAppSelector((state) => state.physicians.byId);
  const healthRecords =
    useAppSelector((state) => state.healthRecords.byId[patientId]) || DEFAULT_OBJECT;
  const appointments =
    useAppSelector((state) => state.appointments.byId[patientId]) || DEFAULT_ARRAY;
  const faxes = useAppSelector((state) => state.fax.patients[patientId]) || DEFAULT_ARRAY;
  const tasks = useAppSelector((state) => state.tasks.byId[patientId]) || DEFAULT_ARRAY;
  const callLogs = useAppSelector((state) => state.resource.calls[patientId]) || DEFAULT_ARRAY;
  const handovers = useAppSelector((state) => state.shifts.handovers);

  const isNP = useAppSelector(IsTypeNP);
  const user = useAppSelector(GetStaff);
  const ownId = user.id;

  const { ownDrafts, otherDrafts } = useMemo(() => {
    const ownDrafts = (healthRecords.appointmentNoteDrafts || []).filter(
      (draft) => draft.author_id === ownId,
    );
    const otherDrafts = (healthRecords.appointmentNoteDrafts || []).filter(
      (draft) => draft.author_id !== ownId,
    );
    return { ownDrafts, otherDrafts };
  }, [healthRecords.appointmentNoteDrafts, ownId]);

  const [editDocumentId, setEditDocumentId] = useState();
  const documentModalState = useMemo(
    () => ({
      documentId: editDocumentId,
      patientId,
      setModalDocument: (id) => setEditDocumentId(id),
    }),
    [editDocumentId, patientId],
  );

  const [openMedicalOrder, setOpenMedicalOrder] = useState();

  const debouncedSearchCPP = useCallback(_.debounce(setSearch, 200), []);

  useEffect(() => {
    debouncedSearchCPP(props.search);
  }, [props.search, healthRecords, appointments, tasks, faxes]);

  const allElementsByDate = useMemo(() => {
    let result = {};

    function addToObj(item, date, type, excludeFunction) {
      if (search && !JSON.stringify(item).toLowerCase().includes(search.toLowerCase())) return;
      if (!excludeFunction || !excludeFunction(item)) {
        const indexDate = getIndexDate(date);
        result[indexDate] = result[indexDate] || [];
        result[indexDate].push({ type, item, date });
      }
    }

    healthRecords.healthRecords?.medical_orders?.forEach((order) =>
      addToObj(order, order.date, 'medical_order'),
    );

    healthRecords.communication_notes?.forEach((note) =>
      addToObj(
        note,
        note.date,
        'communication_note',
        (note) =>
          !note.note.startsWith('Primary Practitioner changed') &&
          !note.note.startsWith('Patient Clearance:'),
      ),
    );

    tasks?.forEach((task) => addToObj(task, task.date, 'task', (task) => task.pinned));

    healthRecords.administered_drugs?.forEach((drug) =>
      addToObj(
        drug,
        drug.value.given_date || drug.value.scheduled_date,
        'administered_drug',
        CPPUtils.isPendingImmunization,
      ),
    );

    healthRecords.documents?.forEach((document) =>
      addToObj(document, document.date, 'document', (document) => document.pinned),
    );

    healthRecords.immunizations?.forEach((immu) =>
      addToObj(
        immu,
        immu.value.given_date || immu.value.scheduled_date,
        'immunization',
        CPPUtils.isPendingImmunization,
      ),
    );

    appointments?.forEach((appointment) => addToObj(appointment, appointment.date, 'appointment'));

    healthRecords.notes?.forEach((note) =>
      addToObj(note, note.value.date, 'note', (note) => note.item_type === 'attachment'),
    );

    (otherDrafts || []).forEach((draft) => addToObj(draft, draft.date, 'note_draft'));

    (healthRecords.notes || []).forEach((note) =>
      addToObj(note, note.value.date, 'note_attachment', (note) => note.item_type !== 'attachment'),
    );

    (healthRecords.prescriptions || []).forEach((rx) => addToObj(rx, rx.date, 'prescription'));

    (healthRecords.referrals || []).forEach((referral) =>
      addToObj(referral, referral.date, 'referral'),
    );

    (healthRecords.referral_requests || []).forEach((request) =>
      addToObj(request, request.date, 'referral_request'),
    );

    (callLogs || []).forEach((log) =>
      addToObj(log, log.date, 'call_log', (log) => {
        return log?.logs?.length === 1 && log.logs[0].role === UserRoleEnum.Patient;
      }),
    );

    (faxes || []).forEach((fax) => addToObj(fax, fax.date, 'fax'));

    if (isNP) {
      (healthRecords.refills || []).forEach((refills) =>
        addToObj(refills, refills.date, 'refills', (refill) => {
          let { status = 'pending' } = refill;
          return status === 'pending';
        }),
      );
    }

    return result;
  }, [
    tasks,
    healthRecords.notes,
    healthRecords.immunizations,
    healthRecords.prescriptions,
    healthRecords.documents,
    healthRecords.referrals,
    healthRecords.refills,
    callLogs,
    appointments,
    faxes,
    search,
    isNP,
    healthRecords.administered_drugs,
    healthRecords.referral_requests,
    healthRecords.communication_notes,
    otherDrafts,
    healthRecords.healthRecords?.medical_orders,
    physicians,
  ]);

  const datesOrdered = useMemo(() => {
    let keys = Object.keys(allElementsByDate).map((number) => parseInt(number));
    keys.sort((a, b) => b - a);
    return keys;
  }, [allElementsByDate]);

  const dates = useMemo(() => {
    let result = [];

    for (let i = 0; i < numberVisible && i < datesOrdered.length; i++) {
      result.push(datesOrdered[i]);
    }
    return result;
  }, [datesOrdered, numberVisible]);

  const hasNextPage = dates.length > 0 && dates.length !== datesOrdered.length;

  function handleLoadMore() {
    setNumberVisible(numberVisible + DEFAULT_VISIBLE);
  }

  const infiniteRef = useInfiniteScroll({
    loading: false,
    hasNextPage,
    onLoadMore: handleLoadMore,
    scrollContainer: 'parent',
  });

  function scrollToTop() {
    cppTop.current.scrollIntoView({ behavior: 'smooth' });
  }

  const unreadDocuments = useMemo(() => {
    return healthRecords.documents?.filter((it) => it.unread_by_physician_id === user.id) || [];
  }, [healthRecords.documents, user.id]);

  const unreadMedicalOrders = useMemo(() => {
    if (user?.physician_type !== PhysicianPhysicianTypeEnum.Nurse) {
      return [];
    }
    return (
      healthRecords.healthRecords?.medical_orders?.filter((it) => !it.read_by?.includes(user.id)) ||
      []
    );
  }, [user?.physician_type, healthRecords.healthRecords, user.id]);

  function onOpenTask(task) {
    setTask(task);
  }

  function renderItem(item, dateIndex) {
    return (
      <span
        className={
          'item animated zoomIn fast item-' +
          item.type +
          (item?.item?.thumbnail ? ' has-thumbnail' : '')
        }
        key={item.item.id || item.date}
      >
        <Item
          item={item}
          patient={patient}
          bubble={props.bubble}
          setActiveCPPTab={setActiveCPPTab}
          index={dateIndex}
          onOpenTask={onOpenTask}
          onClickMedicalOrder={setOpenMedicalOrder}
        />
      </span>
    );
  }

  return (
    <EditDocumentModalContext.Provider value={documentModalState}>
      {task && (
        <TaskEditModal
          key={task?.id}
          task={task}
          editModal={!!task}
          openEditModal={() => setTask()}
          patient={patient}
          staff={physicians[task?.physician_id]}
          showPatient={true}
        />
      )}
      {!!openMedicalOrder && (
        <MedicalOrderModal
          key={!!openMedicalOrder}
          isOpen={!!openMedicalOrder}
          onClose={() => setOpenMedicalOrder()}
          patient={patient}
          order={openMedicalOrder}
          setActiveCPPTab={setActiveCPPTab}
        />
      )}
      <div className="cpp-timeline-wrapper">
        <EditDocumentModal />
        {!props.cppTopRef && (
          <IconButton onClick={scrollToTop} className="scroll-to-top">
            <Icon src="arrow-up-to-line" />
          </IconButton>
        )}
        <div className="cpp-timeline scroll-list">
          <div style={{ float: 'left', clear: 'both' }} ref={cppTop} />
          {confirmationDialog.title && (
            <ConfirmationDialog
              title={confirmationDialog.title}
              message={confirmationDialog.message}
              open={!!confirmationDialog.title}
              onConfirm={() => setConfirmationDialog({ title: undefined, message: undefined })}
            />
          )}
          <div className="list" ref={infiniteRef}>
            <AppointmentBriefingWrapper
              appointments={allElementsByDate[getIndexDate(moment().unix())]?.filter(
                (it) => it.type === 'appointment',
              )}
            />
            <UnreadDocuments
              patient={patient}
              unreadDocuments={unreadDocuments}
              setActiveCPPTab={setActiveCPPTab}
            />
            <UnreadMedicalOrders
              patient={patient}
              orders={unreadMedicalOrders}
              onClickMedicalOrder={setOpenMedicalOrder}
            />
            <DraftElements
              patient={patient}
              drafts={ownDrafts}
              setActiveCPPTab={setActiveCPPTab}
              bubble={props.bubble}
            />
            <PinnedElements
              patient={patient}
              refills={healthRecords.refills}
              tasks={tasks}
              documents={healthRecords.documents}
              setActiveCPPTab={setActiveCPPTab}
              onOpenTask={onOpenTask}
              handovers={handovers}
            />

            {dates.map((date, dateIndex) => {
              let elements = allElementsByDate[date];
              let sets = filterNoteItems(elements);

              return sets.map((set, index) => {
                let { note = {}, items = [], appointment = {} } = set;

                const hasNote =
                  !!note.item || !!items.find(({ type }) => ['note_draft'].includes(type));

                const isFuture = date > moment().endOf('day').unix();

                return (
                  <React.Fragment key={date + index}>
                    <div className="header" id={'header-' + date}>
                      <span className="date">
                        {CalendarUtils.format(date, dateFormat)}
                        {isFuture && (
                          <Icon
                            src="calendar-clock"
                            width="13px"
                            height="13px"
                            alt="Future date"
                            showTooltip={true}
                          />
                        )}
                      </span>
                      {!!note.item && (
                        <AppointmentHeaderLabel
                          physicians={physicians}
                          appointment={appointment.item}
                          note={note.item}
                        />
                      )}
                    </div>
                    <div className={`cpp-content ${hasNote ? 'has-note' : ''}`}>
                      {items
                        .filter((it) => it.type === 'note_draft')
                        .map((it) => renderItem(it, dateIndex))}
                      {!!note.type && (
                        <span className="item animated zoomIn fast item-note">
                          <CPPTimelineAppointmentNote
                            bubble={props.bubble}
                            note={note.item}
                            patient={patient}
                            setActiveCPPTab={setActiveCPPTab}
                            items={items
                              .filter((it) => it.type !== 'note_draft')
                              .map((it) => renderItem(it, dateIndex))}
                          />
                        </span>
                      )}
                      {!note.type &&
                        items
                          .filter((it) => it.type !== 'note_draft')
                          .map((it) => renderItem(it, dateIndex))}
                    </div>
                  </React.Fragment>
                );
              });
            })}
            <DefaultButton
              hide={dates.length === datesOrdered.length}
              name="Show More"
              style={{ margin: '20px 0' }}
              onClick={() => setNumberVisible(numberVisible + DEFAULT_VISIBLE)}
            />
          </div>
        </div>
      </div>
    </EditDocumentModalContext.Provider>
  );
};

const Item = React.memo(
  ({ item, patient, setActiveCPPTab, bubble, index, onOpenTask, onClickMedicalOrder }) => {
    function getContent() {
      if (item.type === 'note_draft') {
        return (
          <CPPTimelineAppointmentNoteDraft
            draft={item.item}
            setActiveCPPTab={setActiveCPPTab}
            bubble={bubble}
            patient={patient}
          />
        );
      }
      if (item.type === 'communication_note')
        return <CPPTimelineCommunicationNote note={item.item} />;
      if (item.type === 'medical_order')
        return (
          <CPPTimelineMedicalOrder
            order={item.item}
            patient={patient}
            onClick={onClickMedicalOrder}
          />
        );
      if (item.type === 'appointment')
        return <CPPTimelineAppointment patient={patient} appointment={item.item} />;
      if (item.type === 'fax') return <CPPTimelineFax patient={patient} fax={item.item} />;
      if (item.type === 'immunization')
        return (
          <CPPTimelineImmunization
            immunization={item.item}
            patient={patient}
            setActiveCPPTab={setActiveCPPTab}
          />
        );
      if (item.type === 'task') return <CPPTimelineTask task={item.item} onOpenTask={onOpenTask} />;
      if (item.type === 'document')
        return (
          <LazyLoad height={190} once={true} offset={200} scrollContainer=".cpp-timeline">
            <CPPTimelineDocument
              document={item.item}
              patient={patient}
              setActiveCPPTab={setActiveCPPTab}
              index={index}
            />
          </LazyLoad>
        );
      if (item.type === 'call_log') return <CPPTimelineCall call={item.item} />;
      if (item.type === 'referral')
        return (
          <CPPTimelineReferral
            referral={item.item}
            patient={patient}
            setActiveCPPTab={setActiveCPPTab}
          />
        );
      if (item.type === 'referral_request')
        return <CPPTimelineReferralRequest request={item.item} />;
      if (item.type === 'prescription')
        return (
          <CPPTimelinePrescription
            prescription={item.item}
            patient={patient}
            setActiveCPPTab={setActiveCPPTab}
          />
        );
      if (item.type === 'note_attachment')
        return (
          <CPPTimelineAppointmentNote
            bubble={bubble}
            note={item.item}
            patient={patient}
            setActiveCPPTab={setActiveCPPTab}
          />
        );
      if (item.type === 'refills') {
        return (
          <CPPTimelinePrescriptionRefill
            refill={item.item}
            patient={patient}
            setActiveCPPTab={setActiveCPPTab}
          />
        );
      }
      if (item.type === 'administered_drug') {
        return (
          <CPPTimelineAdministeredDrug
            drug={item.item}
            patient={patient}
            setActiveCPPTab={setActiveCPPTab}
          />
        );
      }
      return item.type;
    }

    return getContent();
  },
);

const PinnedElements = React.memo(
  ({
    refills = DEFAULT_ARRAY,
    tasks = DEFAULT_ARRAY,
    documents = DEFAULT_ARRAY,
    handovers = DEFAULT_ARRAY,
    patient,
    setActiveCPPTab,
    onOpenTask,
  }) => {
    const isPhlebotomist = useAppSelector(IsTypePhlebotomist);

    const patientId = patient.id;

    const sortedRefills = useMemo(() => {
      if (isPhlebotomist) return [];
      let filtered = refills.filter((refill) => !!refill.status && refill.status === 'pending');
      return _.reverse(_.sortBy(filtered, ['date']));
    }, [refills, isPhlebotomist]);

    const pinnedTasks = useMemo(() => {
      return tasks.filter((task) => task.pinned || TaskUtils.isDue(task));
    }, [tasks]);

    const pinnedDocuments = useMemo(() => {
      return documents.filter(({ pinned }) => pinned);
    }, [documents]);

    const activeHandovers = useMemo(() => {
      return handovers.filter((handover) =>
        handover.patients?.find(({ id, description }) => !!description && id === patientId),
      );
    }, [handovers, patientId]);

    const anyElements =
      sortedRefills.length > 0 ||
      pinnedTasks.length > 0 ||
      pinnedDocuments.length > 0 ||
      activeHandovers.length > 0;
    if (!anyElements) return null;

    return (
      <>
        <div className="header" key="header-refills" id="header-refills">
          <span className="date">
            Pinned Elements
            <Icon
              src={['fad', 'circle-exclamation']}
              className="red"
              iconStyle={{
                '--fa-primary-color': 'white',
                '--fa-secondary-color': 'red',
                '--fa-secondary-opacity': 1.0,
              }}
              width="15px"
            />
          </span>
        </div>
        <div className="cpp-content" key="content-refills">
          {sortedRefills.map((refill) => (
            <span className="item item-refill fast animated zoomIn" key={'refill-' + refill.id}>
              <CPPTimelinePrescriptionRefill
                refill={refill}
                patient={patient}
                setActiveCPPTab={setActiveCPPTab}
              />
            </span>
          ))}
          {pinnedTasks.map((task) => (
            <span className="item item-task fast animated zoomIn" key={'task-' + task.id}>
              <CPPTimelineTask task={task} onOpenTask={onOpenTask} />
            </span>
          ))}
          {pinnedDocuments.map((document) => (
            <span
              className="item fast animated zoomIn item-document has-thumbnail"
              key={'document-' + document.id}
            >
              <CPPTimelineDocument
                document={document}
                patient={patient}
                setActiveCPPTab={setActiveCPPTab}
              />
            </span>
          ))}
          {activeHandovers.map((handover) => (
            <span
              className="item item-handover fast animated zoomIn"
              key={'handover-' + handover.id}
            >
              <CPPTimelineHandover handover={handover} patientId={patientId} />
            </span>
          ))}
        </div>
      </>
    );
  },
);

const UnreadDocuments = ({ patient, unreadDocuments, setActiveCPPTab }) => {
  const items = useMemo(() => {
    return unreadDocuments.map((doc) => ({
      item: doc,
      type: 'document',
    }));
  }, [unreadDocuments]);
  return (
    unreadDocuments.length > 0 && (
      <>
        <div className="header" key="header-unread-documents" id="header-unread-documents">
          <span className="date">
            Unread Documents
            <Icon
              src={['fad', 'circle-exclamation']}
              className="red"
              iconStyle={{
                '--fa-primary-color': 'white',
                '--fa-secondary-color': '#2693d1',
                '--fa-secondary-opacity': 1.0,
              }}
              width="15px"
            />
          </span>
        </div>
        <div className="cpp-content" key="content-unread-documents">
          {items.map((item, index) => (
            <span
              className={
                'item animated zoomIn fast item-' +
                item.type +
                (item?.item?.thumbnail ? ' has-thumbnail' : '')
              }
              key={'unread' + (item.item.id || item.date)}
            >
              <Item item={item} index={index} patient={patient} setActiveCPPTab={setActiveCPPTab} />
            </span>
          ))}
        </div>
      </>
    )
  );
};

const UnreadMedicalOrders = ({ patient, orders, onClickMedicalOrder }) => {
  const items = orders.map((doc) => ({
    item: doc,
    type: 'medical_order',
  }));
  return (
    orders.length > 0 && (
      <>
        <div className="header" id="header-unread-medical-orders">
          <span className="date">
            Unread Medical Orders
            <Icon
              src={['fad', 'circle-exclamation']}
              className="red"
              iconStyle={{
                '--fa-primary-color': 'white',
                '--fa-secondary-color': '#2693d1',
                '--fa-secondary-opacity': 1.0,
              }}
              width="15px"
            />
          </span>
        </div>
        <div className="cpp-content" key="content-unread-documents">
          {items.map((item, index) => (
            <span
              className={
                'item animated zoomIn fast item-' +
                item.type +
                (item?.item?.thumbnail ? ' has-thumbnail' : '')
              }
              key={'unread2' + (item.item.id || item.date)}
            >
              <Item
                item={item}
                index={index}
                patient={patient}
                onClickMedicalOrder={onClickMedicalOrder}
              />
            </span>
          ))}
        </div>
      </>
    )
  );
};

const AppointmentBriefingWrapper = React.memo(({ appointments }) => {
  const isPhysician = useAppSelector((state) => IsPhysician(state));
  const permissions = useAppSelector((state) => state.authentication.permissions);
  const isSuperAdmin = permissions?.includes(PERMISSIONS.super_admin);

  const [appointmentsToShow, closestAppointment] = useMemo(() => {
    const appts = (appointments ?? []).filter(
      (it) =>
        it.item.ai_briefing &&
        (isPhysician || isSuperAdmin) &&
        ![
          APPOINTMENT_STATUS.no_show,
          APPOINTMENT_STATUS.cancelled,
          APPOINTMENT_STATUS.completed,
        ].includes(it.item.status),
    );

    appts.sort((a, b) => a.date - b.date);
    const closest = findClosestAppointment(appts);

    return [appts, closest];
  }, [appointments, isPhysician, isSuperAdmin]);

  if (appointmentsToShow.length === 0) {
    return null;
  }

  return (
    <>
      <div className="header" key="header-appt-briefing" id="header-appt-briefing">
        <div className="date">Carey's Appointment Briefing</div>
      </div>
      <div className="cpp-content gap-2 !grid !pr-[20px]" style={{ gridTemplateColumns: '70%' }}>
        {appointmentsToShow.map((entry) => (
          <div className="appt-briefing-wrapper">
            <AppointmentBriefing
              appointment={entry.item}
              highlighted={entry.item.id === closestAppointment.item.id}
            />
          </div>
        ))}
      </div>
    </>
  );
});

export const AppointmentBriefing = ({ appointment, highlighted = false, className = '' }) => {
  return (
    <div
      className={'appt-briefing ' + className}
      key={`content-appt-briefing-${appointment.id}`}
      data-highlighted={highlighted}
    >
      <div className="flex gap-2 items-center">
        <Icon src="calendar-clock" height="16px" style={{ margin: '0px' }} />{' '}
        <span className="small-title">
          {AppointmentUtils.getAppointmentDescription(appointment)}
          &nbsp;
          {CalendarUtils.format(appointment.date, 'HH:mm A')}
          &nbsp; ({Math.floor(AppointmentUtils.getDuration(appointment) * 60)} minutes)
        </span>
      </div>
      <ReactMarkdown components={{ li: Li }}>{appointment.ai_briefing.trim()}</ReactMarkdown>
    </div>
  );
};

const DraftElements = React.memo(({ drafts = DEFAULT_ARRAY, patient, bubble, setActiveCPPTab }) => {
  const displayDrafts = useMemo(() => {
    return PerformanceUtils.sortBy(drafts);
  }, [drafts]);

  return (
    displayDrafts.length > 0 && (
      <>
        <div className="header" key="header-drafts" id="header-drafts">
          <span className="date">Note Drafts</span>
        </div>
        <div className="cpp-content has-note" key="content-drafts">
          {displayDrafts.map((draft) => (
            <span className="item item-appointment-note-draft">
              <CPPTimelineAppointmentNoteDraft
                key={'draft-' + draft.id}
                bubble={bubble}
                draft={draft}
                patient={patient}
                setActiveCPPTab={setActiveCPPTab}
              />
            </span>
          ))}
        </div>
      </>
    )
  );
});

function filterNoteItems(elements) {
  let notes = elements.filter((el) => el.type === 'note');
  let items = elements.filter((el) => el.type !== 'note');
  let appointments = elements.filter((el) => el.type === 'appointment');

  function calculateTimeDifference(note, other) {
    return Math.abs(note.date - other.date);
  }

  function getFilteredNotes(item) {
    let filteredNotes = item.item?.history?.[0]?.author_id
      ? notes.filter((note) => note.item.author_id === item.item.history[0].author_id)
      : [];
    if (filteredNotes.length === 0) filteredNotes = notes;
    return filteredNotes;
  }

  function getClosest(item) {
    const orderedNotes = _(getFilteredNotes(item))
      .sortBy((note) => calculateTimeDifference(note, item))
      .value();

    if (orderedNotes.length === 0) {
      return;
    }
    if (orderedNotes.length === 1) {
      return orderedNotes[0];
    }

    const [a, b] = orderedNotes;

    if (item.item.physician_id) {
      if (
        b.item.author_id === item.item.physician_id &&
        a.item.author_id !== item.item.physician_id
      ) {
        return b;
      }
    }

    return a;
  }

  let itemsByNote = {};
  let appointmentsByNote = {};

  items.forEach((item) => {
    let closest = getClosest(item);
    if (closest) {
      let closeItems = itemsByNote[closest.item.id] || [];
      itemsByNote[closest.item.id] = [...closeItems, item];
    }
  });

  appointments.forEach((appointment) => {
    let closest = getClosest(appointment, true);
    if (closest) appointmentsByNote[closest.item.id] = appointment;
  });

  if (notes.length === 0) {
    return [
      {
        items: elements,
      },
    ];
  }

  return PerformanceUtils.sortBy(
    notes.map((note) => {
      return {
        note,
        date: note.date,
        items: itemsByNote[note.item.id] || [],
        appointment: (appointmentsByNote[note.id] || {}).appointment,
      };
    }),
    ['date'],
  );
}

const AppointmentHeaderLabel = ({ physicians, appointment, note }) => {
  let description = '';
  let physician;
  let date;
  let noteHidden = false;
  let noteDocuments = 0;

  if (!!appointment) {
    physician = physicians[appointment.physician_id];
    date = CalendarUtils.format(appointment.date, 'h:mm A');
    description = AppointmentUtils.getAppointmentDescription(appointment);
  }

  if (!!note) {
    physician = physicians[note.author_id];
    if (!date) date = CalendarUtils.format(note.value.date, 'h:mm A');
    noteHidden = note.value.hide;
    noteDocuments = (note.documents || []).length;
  }

  let physicianName = physician ? ` ${PhysicianUtils.getName(physician)}` : '';

  if (description) physicianName = ` with${physicianName}`;

  let result = `${description}${physicianName} (${date})`;

  if ((note || {}).waiting_approval) result += ' - Pending Approval';
  else if (noteHidden) result += ' - Hidden';

  if (noteDocuments > 0)
    result = (
      <>
        {result}
        <Icon fontAwesome="paperclip" height="15px" width="15px" />
        {noteDocuments}
      </>
    );

  return (
    <span className="type">
      <span>{result}</span>
    </span>
  );
};

CPPTimeline.propTypes = {
  patient: PropTypes.object.isRequired,
  bubble: PropTypes.object,
  search: PropTypes.string,
  setActiveCPPTab: PropTypes.func,
};

function getIndexDate(unixDate) {
  return moment.unix(unixDate).startOf('day').add(12, 'hour').unix();
}

function findClosestAppointment(appointments) {
  if (appointments.length < 2) {
    return appointments[0];
  }

  const now = moment().unix();

  const idxOfNextAppointment = appointments.findIndex((it) => it.date > now);

  if (idxOfNextAppointment === -1) {
    return _.last(appointments);
  }

  if (idxOfNextAppointment === 0) {
    return appointments[0];
  }

  const deltaBefore = Math.abs(now - appointments[idxOfNextAppointment - 1].date);
  const deltaAfter = Math.abs(now - appointments[idxOfNextAppointment].date);

  return appointments[deltaBefore < deltaAfter ? idxOfNextAppointment - 1 : idxOfNextAppointment];
}

export default CPPTimeline;
